import { useEffect, useRef, useState } from 'react'
import { useCatalogEvent } from 'lib/analytics/catalog'
import styles from './LabCompareSearch.module.css'
import { fetchLabCandidates } from 'lib/dealhunt/client'
import { brandedTitle } from 'lib/dealhunt/url'
import type { DealHuntLabCandidate } from 'lib/dealhunt/types'

/** Debounce for the candidates endpoint (rate-limited at 120 req/min/IP). */
const DEBOUNCE_MS = 275
/** Endpoint 400s below this — also the "start typing" threshold for the dropdown. */
const MIN_QUERY = 2

interface LabCompareSearchProps {
  /** Current PDP's taxonomy node id, so the product never appears in its own picker. */
  excludeNodeId?: string
  /** ClearBuy ids already present as chips — their rows show "Added" (picking one
   *  re-activates the existing chip instead of duplicating it; the parent resolves that). */
  existingClearbuyIds: ReadonlySet<number>
  /** All overlay slots in use — picks are ignored upstream, so surface it here instead. */
  atCap: boolean
  onPick: (candidate: DealHuntLabCandidate) => void
}

/**
 * Free-search typeahead for the lab-data compare picker: search ANY lab-tested product
 * (DealHunt `/api/search/lab-candidates`) and hand the pick to `LabData`, which owns
 * chips/overlay state. Renders in the chip flow: a chip-sized pill on desktop with a
 * floating dropdown; a full-width field on mobile whose results expand in-flow.
 */
const LabCompareSearch: React.VFC<LabCompareSearchProps> = ({
  excludeNodeId,
  existingClearbuyIds,
  atCap,
  onPick,
}) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<DealHuntLabCandidate[]>([])
  const [highlight, setHighlight] = useState(-1)
  const rootRef = useRef<HTMLDivElement>(null)
  const { track } = useCatalogEvent()

  const trimmed = query.trim()
  const searchable = trimmed.length >= MIN_QUERY

  // Debounced fetch, cancelled by the next keystroke or close.
  useEffect(() => {
    if (!open || !searchable) {
      setResults([])
      setLoading(false)
      return undefined
    }
    const ctrl = new AbortController()
    const timer = setTimeout(() => {
      setLoading(true)
      fetchLabCandidates(trimmed, { excludeNodeId }, ctrl.signal)
        .then(candidates => {
          setResults(candidates)
          setHighlight(-1)
          track('product_page_lab_compare_search', {
            search_term: trimmed,
            result_count: candidates.length,
          })
        })
        .catch(() => undefined) // aborted — the next request owns the state
        .finally(() => setLoading(false))
    }, DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      ctrl.abort()
    }
  }, [open, searchable, trimmed, excludeNodeId, track])

  // Click outside closes the dropdown.
  useEffect(() => {
    if (!open) {
      return undefined
    }
    const onClick = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const pick = (candidate: DealHuntLabCandidate): void => {
    onPick(candidate)
    setQuery('')
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (!results.length) {
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight(h => Math.min(h + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const candidate = results[highlight >= 0 ? highlight : 0]
      if (candidate) {
        pick(candidate)
      }
    }
  }

  const showDropdown = open && (searchable || atCap)

  return (
    <div ref={rootRef} className={styles.root}>
      <div className={styles.field}>
        <svg
          className={styles.mag}
          viewBox="0 0 24 24"
          style={{ fill: 'none' }}
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.8-3.8" />
        </svg>
        <input
          type="text"
          className={styles.input}
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="lab-compare-results"
          aria-label="Search any lab-tested product to compare"
          placeholder="Search any lab-tested product…"
          autoComplete="off"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={e => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      {showDropdown && (
        <div className={styles.dropdown} id="lab-compare-results" role="listbox">
          {atCap ? (
            <p className={styles.notice}>
              {'All 4 overlay slots are in use — remove a product to add another.'}
            </p>
          ) : (
            <>
              {results.map((candidate, i) => {
                const already = existingClearbuyIds.has(candidate.clearbuyProductId)
                return (
                  <button
                    key={candidate.nodeId}
                    type="button"
                    role="option"
                    aria-selected={i === highlight}
                    className={styles.hit}
                    data-highlight={i === highlight}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(candidate)}
                  >
                    <span className={styles.hitThumb}>
                      {candidate.imageUrl && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={candidate.imageUrl} alt="" loading="lazy" />
                      )}
                    </span>
                    <span className={styles.hitName}>
                      {brandedTitle(candidate.brand, candidate.name)}
                    </span>
                    {already && <span className={styles.hitTag}>{'Added'}</span>}
                  </button>
                )
              })}
              {!results.length && (
                <p className={styles.notice}>
                  {loading ? 'Searching…' : `No lab-tested products match “${trimmed}”.`}
                </p>
              )}
              <p className={styles.foot}>
                {'Only products with SoundGuys lab measurements appear here.'}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default LabCompareSearch
